import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, InputBase } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Step3FTEAssociate from "./Step3FTEAssociate";
import Step3FTEBar from "./Step3FTEBar";
import { checkAllocationOverlap } from "../../../../../../api/associates";

const Step3FTE = ({
  associate,
  fte,
  onFteChange,
  skills,
  selectedSkills,
  onSkillToggle,
  skillsError,
  startDate,
  onStartChange,
  endDate,
  onEndChange,
  notes,
  onNotesChange,
  errors,
  readOnly,
  position,
  currentFte,
  excludeRequestId,
  excludeParentRequestId,
  skillsLoading,
  onOverlapError,
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const [overlapError, setOverlapError] = useState(null);
  const [overlapChecking, setOverlapChecking] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (readOnly) return;
    const associateId = associate?.associateId;
    if (!associateId || !startDate || !endDate || startDate >= endDate || !fte) {
      setOverlapError(null);
      onOverlapError?.(null);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setOverlapChecking(true);
      try {
        const result = await checkAllocationOverlap(associateId, startDate, endDate, fte, excludeRequestId, excludeParentRequestId);
        if (result.overlaps) {
          const msg = `Overlaps with existing allocation ${result.conflictStart} → ${result.conflictEnd}`;
          setOverlapError(msg);
          onOverlapError?.(msg);
        } else {
          setOverlapError(null);
          onOverlapError?.(null);
        }
      } catch {
        setOverlapError(null);
        onOverlapError?.(null);
      } finally {
        setOverlapChecking(false);
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [associate?.associateId, startDate, endDate, fte, readOnly, excludeRequestId, excludeParentRequestId]);

  return (
    <Box>
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "8px",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: t.text3,
          mb: "8px",
        }}
      >
        Associate & Allocation
      </Typography>

      <Step3FTEAssociate associate={associate} t={t} />

      <Box
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "flex-start",
          padding: "8px 10px",
          mb: "10px",
          background: t.accent2,
          border: `1px solid ${t.accent}33`,
          borderRadius: "3px",
        }}
      >
        <Box
          component="span"
          sx={{ fontSize: "12px", lineHeight: 1.4, flexShrink: 0, mt: "1px" }}
        >
          💡
        </Box>
        <Typography
          sx={{
            fontSize: "11px",
            color: t.text2,
            lineHeight: 1.6,
            fontWeight: 400,
          }}
        >
          If you choose a partial allocation, the remaining FTE can be allocated
          to another MAC. This is the quantum of allocation to your MAC and{" "}
          <Box component="span" sx={{ color: t.accent, fontWeight: 600 }}>
            not
          </Box>{" "}
          to be confused with billing.
        </Typography>
      </Box>

      <Step3FTEBar
        associate={associate}
        fte={fte}
        onFteChange={onFteChange}
        readOnly={readOnly}
        currentFte={currentFte}
        t={t}
      />

     {/* Skills */}
{skillsLoading ? (
  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text3, fontStyle: "italic", mb: "16px" }}>
    Loading skills...
  </Typography>
) : skills?.length > 0 ? (
  <>
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.15em", textTransform: "uppercase", color: t.text3, mb: "3px" }}>
      Deployed Skills <span style={{ color: t.accent }}>*</span>
    </Typography>
    <Typography sx={{ fontSize: "11px", color: t.text3, mb: "8px", fontWeight: 300 }}>
      Select skills from this associate's Zoho profile that will be specifically deployed on this engagement
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: skillsError ? "6px" : "16px" }}>
      {skills.map((sk) => {
        const isPicked =
          selectedSkills.includes(sk.skillId) ||
          selectedSkills.includes(sk.skillName);
        return (
          <Box
            key={sk.skillId ?? sk}
            onClick={() => {
              if (readOnly) return;
              onSkillToggle?.(sk.skillId ?? sk.skillName ?? sk);
            }}
            sx={{
              padding: "4px 10px",
              borderRadius: "2px",
              cursor: "pointer",
              border: isPicked ? `1px solid ${t.accent}66` : `1px solid ${t.border2}`,
              background: isPicked ? t.accent2 : t.surface2,
              transition: "all 0.12s",
              "&:hover": { borderColor: t.accent + "44" },
            }}
          >
            <Typography sx={{ fontSize: "11px", color: isPicked ? t.accent : t.text2 }}>
              {sk.skillName ?? sk}
            </Typography>
          </Box>
        );
      })}
    </Box>
    {skillsError && (
      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#c84040", mb: "16px" }}>
        {skillsError}
      </Typography>
    )}
  </>
) : null}

      {/* Dates */}
      {/* Position date range hint */}
      {(position?.startDate || position?.endDate) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            mb: "8px",
            padding: "6px 10px",
            background: t.surface2,
            border: `1px solid ${t.border2}`,
            borderRadius: "3px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "8px",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: t.text3,
              flexShrink: 0,
            }}
          >
            Position valid between:
          </Typography>
          <Typography
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "10px",
              color: t.accent,
            }}
          >
            {position?.startDate ? position.startDate.split("T")[0] : "—"}
            {" → "}
            {position?.endDate ? position.endDate.split("T")[0] : "—"}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px",
          mb: overlapError ? "6px" : "12px",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "8px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: t.text3,
              mb: "5px",
            }}
          >
            Start Date
          </Typography>
          <Box
            sx={{
              background: t.surface2,
              border: `1px solid ${overlapError || errors?.startDate ? "#c84040" : overlapChecking ? t.accent + "88" : t.border2}`,
              borderRadius: "3px",
              padding: "7px 10px",
              "&:focus-within": { borderColor: t.accent },
            }}
          >
            <InputBase
              value={startDate}
              onChange={(e) => {
                if (readOnly) return;
                onStartChange?.(e.target.value);
              }}
              inputProps={{
                type: "date",
                min: position?.startDate ? position.startDate.split("T")[0] : undefined,
                max: position?.endDate ? position.endDate.split("T")[0] : undefined,
                style: { colorScheme: isDark ? "dark" : "light" },
              }}
              sx={{
                width: "100%",
                fontFamily: "'DM Mono', monospace",
                fontSize: "12px",
                color: t.text,
              }}
            />
          </Box>
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "8px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: t.text3,
              mb: "5px",
            }}
          >
            End Date
          </Typography>
          <Box
            sx={{
              background: t.surface2,
              border: `1px solid ${overlapError || errors?.endDate ? "#c84040" : overlapChecking ? t.accent + "88" : t.border2}`,
              borderRadius: "3px",
              padding: "7px 10px",
              "&:focus-within": { borderColor: t.accent },
            }}
          >
            <InputBase
              value={endDate}
              onChange={(e) => {
                if (readOnly) return;
                onEndChange?.(e.target.value);
              }}
              inputProps={{
                type: "date",
                min: position?.startDate ? position.startDate.split("T")[0] : undefined,
                max: position?.endDate ? position.endDate.split("T")[0] : undefined,
                style: { colorScheme: isDark ? "dark" : "light" },
              }}
              sx={{
                width: "100%",
                fontFamily: "'DM Mono', monospace",
                fontSize: "12px",
                color: t.text,
              }}
            />
          </Box>
        </Box>
      </Box>

      {overlapError && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "12px" }}>
          <Typography sx={{ fontSize: "11px", color: "#c84040", fontFamily: "'DM Mono', monospace" }}>
            {overlapError}
          </Typography>
        </Box>
      )}
      {overlapChecking && !overlapError && (
        <Box sx={{ mb: "12px" }}>
          <Typography sx={{ fontSize: "10px", color: t.text3, fontFamily: "'DM Mono', monospace", fontStyle: "italic" }}>
            Checking for overlaps...
          </Typography>
        </Box>
      )}

      {/* Notes */}
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "8px",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: t.text3,
          mb: "5px",
        }}
      >
        Notes
      </Typography>
      <Box
        sx={{
          background: t.surface2,
          border: `1px solid ${t.border2}`,
          borderRadius: "3px",
          padding: "7px 10px",
          "&:focus-within": { borderColor: t.accent },
        }}
      >
        <InputBase
          multiline
          rows={3}
          value={notes}
          onChange={(e) => {
            if (readOnly) return;
            onNotesChange?.(e.target.value);
          }}
          placeholder="Context for PMO review..."
          sx={{
            width: "100%",
            fontFamily: "'Inter', sans-serif",
            fontSize: "12px",
            color: t.text,
            "& textarea::placeholder": { color: t.text3 },
          }}
        />
      </Box>
    </Box>
  );
};

export default Step3FTE;
